import type { GoodsOption } from 'shared/src/api';
import { useLogGoodsAnalyticsEvent } from '../../../hooks/useLogGoodsAnalyticsEvent';
import { useCallback, useMemo } from 'react';
import { bridge } from 'shared/src/utils';
import { useGoodsBasic } from '../../../queries/useGoodsBasic';
import { useGoodsSno } from '../../../hooks/useGoodsSno';
import { useGoodsOptionsPricing } from '../queries/useGoodsOptionsPricing';
import { useSelectOptionContext } from '../context/SelectOptionsContext';
import { useGoodsFrom } from '../../../hooks/useGoodsFrom';

export const useLogClickCheckOut = (params: { selected: GoodsOption[] }) => {
  const logGoodsAnalytics = useLogGoodsAnalyticsEvent();
  const { data: goodsBasic } = useGoodsBasic();
  const goodsSno = useGoodsSno();
  const { data: pricing } = useGoodsOptionsPricing({
    options: params.selected.map(option => ({
      sno: option.sno,
      count: option.purchase_count ?? 1,
    })),
  });

  const issuedCoupons = useMemo(() => {
    if (!pricing) return [];
    return pricing.coupon_list.filter(coupon => coupon.item.is_issued);
  }, [pricing]);
  const { mode: selectOptionMode } = useSelectOptionContext();
  // CLICK_BUY_GOODS는 직접 bridge 호출이라 getDefaults(FROM)를 우회함 → 출처 화면 명시 주입
  const from = useGoodsFrom();

  return useCallback(() => {
    params.selected.forEach(selected => {
      logGoodsAnalytics({
        name: 'CLICK_CHECKOUT_PER_GOODS',
        analytics: {
          GOODS_OPTION_SNO: selected.sno.toString(),
          ORDER_QUANTITY: selected.stock,
          IS_DELAY: selected.is_delayed ? '1' : '0',
          ...(selectOptionMode === 'gift' ? { KIND: 'GIFT' } : {}),
        },
      });
    });

    bridge.logAnalyticsEvent({
      name: 'CLICK_BUY_GOODS',
      analytics: {
        SCREEN: 'GOODS_DETAIL',
        ...(from != null ? { FROM: from } : {}),
        DELIVERY_TYPE: goodsBasic?.delivery_type_info.delivery_type,
        GOODS_SNO: goodsSno.toString(),
        MARKET_SNO: goodsBasic?.market.sno.toString(),
        EVENT_TYPE: 'CLICK_BUY_GOODS',
        ...(selectOptionMode === 'gift' ? { KIND: 'GIFT' } : {}),
      },
    });

    logGoodsAnalytics({
      name: 'CLICK_CHECKOUT',
      analytics: {
        APPLIED_COUPON_COUNT: issuedCoupons.length,
        APPLIED_COUPON_ISSUER_TYPE: issuedCoupons.map(
          coupon =>
            (coupon.logging.analytics as { ISSUER_TYPE: string }).ISSUER_TYPE,
        ),
        ...(selectOptionMode === 'gift' ? { KIND: 'GIFT' } : {}),
      },
    });
  }, [
    from,
    goodsBasic?.delivery_type_info.delivery_type,
    goodsBasic?.market.sno,
    goodsSno,
    issuedCoupons,
    logGoodsAnalytics,
    params.selected,
    selectOptionMode,
  ]);
};
